import { FormikProps } from "formik";
import { MORE_MAPPING_FORM_DATA, MoreFieldMappingItem, RECIPE_FORM_DATA, TITLE_DESCRIPTION } from "src/interfaces/Template.interface";
import { PlatformDataType } from "src/interfaces/Platform.interface";
import { CategoryMappingChangeData } from "../CategoryMappingEngine.previous";
import { PriceRuleFormValue } from "../hooks/usePriceModifierFields.previous";
import {
  buildMoreMappingPayload,
  buildMoreMappingSaveEntries,
  buildMoreMappingClearJobs,
  getMissingMandatoryMappingField,
  getMoreMappingFieldIdsToClearOnEdit,
  omitMoreMappingRefsForClearedFields,
} from "../../utils/quicklistMoreMappingUtils.previous";
import { resolveQuicklistPricePayload } from "../../utils/quicklistPriceModifierUtils.previous";
import { getPriceRuleValidationError } from "../../../utils/priceModifierValidation";
import { prepareDescriptionValueForSave } from "src/utils/CustomEditor";
import {
  resolveExistingMoreMappingTemplateId,
  toRecipeOtherTemplates,
  upsertMoreMappingRef,
  type MoreMappingIdRef,
} from "../../../components/utils/quickListMoreMappingIdentity";
import {
  updateTitleDescriptionTemplateAsync,
  savePlatformTitleDescriptionTemplateAsync,
  updateCategoryTemplateDataAsync,
  postFieldDataAttributesAsync,
  updateMoreMappingAsync,
  createMoreMappingAsync,
  updateRecipeAsync,
  deleteRecipeMappingItemAsync,
} from "src/redux/actions/templateAndPricingActions";
import {
  updatePlatformPriceAsync,
  savePlatformPriceAsync,
} from "src/redux/actions/productPricingAction";

export interface UpdateQuickListContext {
  editingRecipeId: number;
  editSubTemplateIds: {
    titleId: number | null;
    priceId: number | null;
    categoryId: number | null;
    moreMappingIds: MoreMappingIdRef[];
  };
  finalRuleName: string;
  status?: "ACTIVE" | "DRAFT";
  platformId: number | undefined;
  selectedSecondaryPlatform: PlatformDataType | null;
  formikRef: React.RefObject<FormikProps<RECIPE_FORM_DATA>>;
  engineState: CategoryMappingChangeData;
  selectedAttributes: any[];
  titleInput: string;
  editorData: string;
  descriptionHtmlSource?: boolean;
  tagsInput: string;
  skuInput: string;
  showIsDefault: boolean;
  titleAdvanceSetting: any;
  descriptionAdvanceSetting: any;
  priceTemplateFields: any[];
  priceRules: PriceRuleFormValue[];
  repricing: { type: string; value: number };
  allValues: MORE_MAPPING_FORM_DATA[];
  moreFieldMappingData: MoreFieldMappingItem[];
  checkFormField: string | undefined;
  dispatch: any;
  toast: any;
  setIsSaving: (val: boolean) => void;
  exitEditMode: () => void;
  setRefreshTriggerCounter: (updater: (prev: number) => number) => void;
  selectedRowsForDeletion: (number | null)[];
  setSelectedRowsForDeletion: (val: (number | null)[]) => void;
}

export const executeQuickListUpdate = async (context: UpdateQuickListContext) => {
  const {
    editingRecipeId,
    editSubTemplateIds,
    finalRuleName,
    status = "ACTIVE",
    platformId,
    selectedSecondaryPlatform,
    formikRef,
    engineState,
    selectedAttributes,
    titleInput,
    editorData,
    descriptionHtmlSource = false,
    tagsInput,
    skuInput,
    showIsDefault,
    titleAdvanceSetting,
    descriptionAdvanceSetting,
    priceTemplateFields,
    priceRules,
    repricing,
    allValues,
    moreFieldMappingData,
    checkFormField,
    dispatch,
    toast,
    setIsSaving,
    exitEditMode,
    setRefreshTriggerCounter,
    selectedRowsForDeletion,
    setSelectedRowsForDeletion,
  } = context;

  const currentStatus = status ?? "ACTIVE";
  const formikValues = formikRef.current?.values;

  if (currentStatus === "ACTIVE") {
    if (engineState.categoryId === null) {
      toast.error("Please select a category to proceed");
      return;
    }

    if (!formikValues) {
      toast.error("Please fill Product Criteria Form");
      return;
    }

    const hasInvalidCondition = formikValues.productRecipes?.some(
      (item) => item?.checkValue == null || !String(item.checkValue).trim()
    );

    if (hasInvalidCondition) {
      toast.error("Please Add Value for Condition Evaluator");
      setIsSaving(false);
      return;
    }

    const missingMandatoryField = getMissingMandatoryMappingField(
      allValues,
      moreFieldMappingData ?? []
    );
    if (missingMandatoryField) {
      toast.error(
        `Please provide a mapping or default value for the required field: ${missingMandatoryField.display_name}`
      );
      return;
    }
  }

  if (priceTemplateFields.length > 0) {
    const priceRuleValidationError = getPriceRuleValidationError(priceRules);
    if (priceRuleValidationError) {
      toast.error(priceRuleValidationError);
      return;
    }
  }

  setIsSaving(true);
  const errors: string[] = [];

  try {
    // 1. Build Payloads
    const templateDesPayload: any = {
      platform_id: platformId,
      name: finalRuleName,
      status: currentStatus,
      title_value_string: titleInput,
      description_value_string: prepareDescriptionValueForSave(
        editorData || (editSubTemplateIds.titleId ? " " : editorData),
        { htmlSource: descriptionHtmlSource }
      ),
      tags_value_string: tagsInput,
      sku_value_string: skuInput,
      isDefault: showIsDefault,
      title_processing_config: {
        isRemoveHtml: titleAdvanceSetting.isRemoveHtml,
        isTranslate: titleAdvanceSetting.isTranslate,
        translate_language: titleAdvanceSetting.translate_language,
        isRephrasing: titleAdvanceSetting.isRephrasing,
        rephrasing_limit: titleAdvanceSetting.rephrasing_limit,
      },
      description_processing_config: {
        isRemoveHtml: descriptionAdvanceSetting.isRemoveHtml,
        isTranslate: descriptionAdvanceSetting.isTranslate,
        translate_language: descriptionAdvanceSetting.translate_language,
        isRephrasing: descriptionAdvanceSetting.isRephrasing,
        rephrasing_limit: descriptionAdvanceSetting.rephrasing_limit,
      },
    };

    const resolvedPricePayload = resolveQuicklistPricePayload({
      priceTemplateFields,
      priceRules,
      repricing,
      ruleName: finalRuleName,
      platformId,
      mode: editSubTemplateIds.priceId ? 'update' : 'create',
    });
    const pricePayload = resolvedPricePayload
      ? Array.isArray(resolvedPricePayload)
        ? resolvedPricePayload.map((payload) => ({ ...payload, status: currentStatus }))
        : { ...resolvedPricePayload, status: currentStatus }
      : null;

    const categoryPayload = {
      platform_id: selectedSecondaryPlatform?.id,
      isDefault: false,
      name: finalRuleName,
      status: currentStatus,
      category_data: engineState.categoryId,
      attributes: selectedAttributes,
    };

    const clearedFieldIds = getMoreMappingFieldIdsToClearOnEdit(
      allValues,
      moreFieldMappingData ?? [],
      editSubTemplateIds.moreMappingIds
    );
    const clearedFieldIdSet = new Set(clearedFieldIds.map((id) => Number(id)));

    const moreMappingSaveEntries = buildMoreMappingSaveEntries(
      allValues,
      moreFieldMappingData ?? []
    ).filter((entry) => !clearedFieldIdSet.has(Number(entry.fieldMeta.id)));

    const moreMappingClearJobs = buildMoreMappingClearJobs(
      clearedFieldIds,
      editSubTemplateIds.moreMappingIds,
      moreFieldMappingData ?? [],
      finalRuleName,
      selectedSecondaryPlatform?.id ?? platformId ?? null
    );

    const mappingPayloads = moreMappingSaveEntries.map((entry, i) => {
      const built = buildMoreMappingPayload(
        entry,
        finalRuleName,
        i,
        selectedSecondaryPlatform?.id ?? platformId ?? null
      );
      return {
        ...built,
        // Keep the hydrated template name on update so PUT does not 400
        // "Rule name already exists" from a regenerated name.
        name: entry.values.name || built.name,
        status: currentStatus,
      };
    });

    // 2. Execute Sub-Template Updates / Creates
    const updateStageApis: { name: string; call: () => Promise<any> }[] = [];

    // Title/Description
    if (editSubTemplateIds.titleId) {
      updateStageApis.push({
        name: "Title/Description",
        call: () =>
          dispatch(
            updateTitleDescriptionTemplateAsync({
              platform_id: editSubTemplateIds.titleId + "",
              payload: templateDesPayload,
            })
          ),
      });
    } else {
      updateStageApis.push({
        name: "Title/Description",
        call: () => dispatch(savePlatformTitleDescriptionTemplateAsync(templateDesPayload)),
      });
    }

    // Price ΓÇö skip create when the user left price untouched; still update an existing template.
    if (editSubTemplateIds.priceId) {
      if (pricePayload) {
        updateStageApis.push({
          name: "Price Modifier",
          call: () =>
            dispatch(
              updatePlatformPriceAsync({
                template_id: editSubTemplateIds.priceId!,
                payload: pricePayload,
              })
            ),
        });
      }
    } else if (pricePayload) {
      updateStageApis.push({
        name: "Price Modifier",
        call: () => dispatch(savePlatformPriceAsync(pricePayload)),
      });
    }

    // Category
    if (editSubTemplateIds.categoryId) {
      updateStageApis.push({
        name: "Category Mapping",
        call: () =>
          dispatch(
            updateCategoryTemplateDataAsync({
              category_template: editSubTemplateIds.categoryId!,
              payloadupdate: categoryPayload,
            })
          ),
      });
    } else {
      updateStageApis.push({
        name: "Category Mapping",
        call: () => dispatch(postFieldDataAttributesAsync(categoryPayload)),
      });
    }

    const moreMappingJobs = mappingPayloads.map((mp, i) => ({
      mp,
      entry: moreMappingSaveEntries[i],
      existingTemplateId: resolveExistingMoreMappingTemplateId(
        moreMappingSaveEntries[i],
        editSubTemplateIds.moreMappingIds
      ),
    }));

    // More Mappings ΓÇö PUT when a template already exists; POST only for a new filled section.
    moreMappingJobs.forEach((job, i) => {
      if (job.existingTemplateId) {
        updateStageApis.push({
          name: `More Mapping #${i + 1}`,
          call: () =>
            dispatch(
              updateMoreMappingAsync({
                platforn_field_template_id: job.existingTemplateId + "",
                payload: job.mp,
              })
            ),
        });
      } else {
        updateStageApis.push({
          name: `More Mapping #${i + 1}`,
          call: () => dispatch(createMoreMappingAsync(job.mp)),
        });
      }
    });

    // Cleared optional fields ΓÇö wipe existing templates and unlink on master recipe.
    moreMappingClearJobs.forEach((job, i) => {
      updateStageApis.push({
        name: `More Mapping Clear #${i + 1}`,
        call: () =>
          dispatch(
            updateMoreMappingAsync({
              platforn_field_template_id: job.existingTemplateId + "",
              payload: { ...job.payload, status: currentStatus },
            })
          ),
      });
    });

    const results = await Promise.allSettled(updateStageApis.map((api) => api.call()));

    results.forEach((res, i) => {
      if (res.status === "rejected") {
        errors.push(`${updateStageApis[i].name}: update failed`);
      }
    });

    if (errors.length > 0) {
      toast.error(`Some updates failed:\n${errors.join("\n")}`);
      console.error("Γ¥î Failed Update APIs:", errors);
      return;
    }

    // 2.5 Delete removed condition rows
    if (selectedRowsForDeletion && selectedRowsForDeletion.length > 0) {
      const deletePromises = selectedRowsForDeletion.map((id) => {
        if (id !== null) {
          return dispatch(deleteRecipeMappingItemAsync({ templateMappingItemId: id + "" }));
        }
        return Promise.resolve();
      });
      try {
        await Promise.all(deletePromises);
        setSelectedRowsForDeletion([]);
      } catch (err) {
        console.error("Failed to delete some condition rows", err);
      }
    }

    // 3. Update Master Recipe
    // Filter out deleted rows from mapping_data
    const mappingDataToSave = formikValues?.productRecipes.filter(
      (item) => !selectedRowsForDeletion.includes(item.id)
    );

    const extractCreatedId = (payload: any): number | null => {
      if (!payload) return null
      if (Array.isArray(payload)) {
        return payload[0]?.price_template?.id || payload[0]?.id || null
      }
      return payload.id ?? payload.price_template?.id ?? null
    }

    let nextTitleId = editSubTemplateIds.titleId
    let nextPriceId = editSubTemplateIds.priceId
    let nextCategoryId = editSubTemplateIds.categoryId
    let nextMoreRefs: MoreMappingIdRef[] = [...editSubTemplateIds.moreMappingIds]

    results.forEach((res, i) => {
      if (res.status !== "fulfilled") return
      const apiName = updateStageApis[i].name
      const payload = (res.value as any)?.payload
      const createdId = extractCreatedId(payload)

      if (apiName === "Title/Description" && createdId) nextTitleId = createdId
      if (apiName === "Price Modifier" && createdId) nextPriceId = createdId
      if (apiName === "Category Mapping" && createdId) nextCategoryId = createdId

      if (apiName.startsWith("More Mapping #")) {
        const jobIndex = Number(apiName.replace("More Mapping #", "")) - 1
        const job = moreMappingJobs[jobIndex]
        if (!job) return
        const templateId = job.existingTemplateId || createdId
        const fieldId =
          job.entry.fieldMeta?.id ??
          job.entry.values.platform_field ??
          null
        if (templateId) {
          nextMoreRefs = upsertMoreMappingRef(nextMoreRefs, {
            fieldId: fieldId != null ? Number(fieldId) : null,
            templateId: Number(templateId),
          })
        }
      }
    })

    const masterRecipePayload = {
      platform: selectedSecondaryPlatform?.id,
      name: finalRuleName,
      status: currentStatus,
      type: "QUICKLIST",
      title_description_template: nextTitleId,
      price_template: nextPriceId,
      category_template: nextCategoryId,
      check_data_conjunction: formikValues?.dataConjunction,
      mapping_data: mappingDataToSave?.map((item) => ({
        id: item.id,
        check_platform_field: item.checkPlatformField || checkFormField,
        check_operator: item.checkOperator ?? "CONTAINS",
        check_conjunction: item.checkConjunction,
        check_value: item.checkValue,
      })),
      other_templates: toRecipeOtherTemplates(
        omitMoreMappingRefsForClearedFields(nextMoreRefs, clearedFieldIds)
      ),
    };

    const updateResult: any = await dispatch(
      updateRecipeAsync({
        recipeId: editingRecipeId + "",
        payload: masterRecipePayload,
      })
    );

    if (updateResult?.payload?.error || updateResult?.error) {
      toast.error("Failed to update Listing Profile rule.");
    } else {
      toast.success("Listing Profile updated successfully!");
      exitEditMode();
      setRefreshTriggerCounter((prev) => prev + 1);
    }
  } catch (err) {
    console.error("Error during Quick List update:", err);
    toast.error("Unexpected error during update.");
  } finally {
    setIsSaving(false);
  }
};
